import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, BadgeCheck, CalendarClock, Crown, Gift, EyeOff, UserPlus, UserCheck, Users } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MediaImage, LockedOverlay } from "@/components/Media";
import { fmtMoney, fmtDate } from "@/components/Peep";
import { TipDialog } from "@/components/TipDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function CreatorProfilePage() {
  const { handle } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isReview = user?.role === "platform_owner_review";
  const isGuestReviewer = user?.role === "external_premium_reviewer";
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [fbusy, setFbusy] = useState(false);
  const toggleFollow = async () => {
    setFbusy(true);
    try {
      const { data } = d.following ? await api.delete(`/creators/${d.creator.id}/follow`) : await api.post(`/creators/${d.creator.id}/follow`);
      setD({ ...d, following: data.following, follower_count: data.follower_count });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setFbusy(false);
    }
  };
  const load = () => api.get(`/creators/${handle}`).then((r) => setD(r.data)).catch(() => setErr("Creator not found."));
  useEffect(() => { load(); }, [handle]); // eslint-disable-line react-hooks/exhaustive-deps
  const unpublish = async (it) => {
    const reason = window.prompt(`Unpublish "${it.title}"? Enter the moderation reason (logged).`);
    if (!reason || reason.trim().length < 3) return;
    try {
      await api.post(`/review/content/${it.id}/unpublish`, { reason: reason.trim() });
      toast.success("Item unpublished and logged");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const pay = async (path, body) => {
    try {
      const { data } = await api.post(path, body);
      navigate(data.hosted_url);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const [tipOpen, setTipOpen] = useState(false);
  const [post, setPost] = useState(null);
  const tip = () => setTipOpen(true);
  const open = (it) => {
    if (it.locked && it.access_level === "ppv" && it.ppv_price) return pay(`/pay/ppv/${it.id}`);
    if (it.type === "post") return it.locked ? navigate("/app/membership") : setPost(it);
    navigate(it.type === "video" ? "/app/videos" : "/app/photos");
  };
  if (err) return <p data-testid="creator-profile-error" className="p-6 text-sm text-[#a7a0af]">{err}</p>;
  if (!d) return <p className="p-6 text-sm text-[#a7a0af]">Loading…</p>;
  const { creator: c, plan } = d;
  return (
    <div data-testid="creator-profile-screen">
      <TipDialog open={tipOpen} onOpenChange={setTipOpen} creator={c} onCheckout={(url) => navigate(url)} />
      <div className="relative h-56 w-full overflow-hidden">
        {c.cover_url ? <MediaImage url={c.cover_url} className="h-full w-full object-cover object-[center_20%]" /> : <div className="h-full w-full bg-gradient-to-br from-[#6a00ff]/60 via-[#080808] to-[#d42be8]/50" />}
        <div className="absolute inset-0 bg-gradient-to-b from-[#080808]/40 to-[#080808]" />
        <button onClick={() => navigate(-1)} className="absolute left-5 top-6 text-white" aria-label="Back"><ArrowLeft size={22} /></button>
      </div>
      <div className="relative -mt-14 px-5">
        <div className="h-20 w-20 overflow-hidden rounded-full ring-2 ring-[#d42be8]">
          {c.avatar_url ? <MediaImage url={c.avatar_url} className="h-full w-full object-cover" /> : <div className="h-full w-full bg-gradient-to-br from-[#6a00ff] to-[#d42be8]" />}
        </div>
        <h1 data-testid="creator-profile-name" className="mt-3 text-2xl font-extrabold">{c.display_name}</h1>
        <p className="text-sm text-[#a7a0af]">@{c.handle}</p>
        <p data-testid="creator-profile-followers" className="mt-1 flex items-center gap-1 text-sm"><Users size={14} className="text-[#c77dff]" /> <span className="font-semibold">{d.follower_count}</span> {d.follower_count === 1 ? "follower" : "followers"}</p>
        {(c.categories || []).length > 0 && <div className="mt-2 flex flex-wrap gap-1.5">{c.categories.map((x) => <span key={x} className="rounded-full border border-[#8a2be2]/50 px-2 py-0.5 text-[0.6rem] text-[#e0c2ff]">{x}</span>)}</div>}
        {c.bio && <p className="mt-2 text-sm text-white/85">{c.bio}</p>}
        <div className="mt-3 flex flex-wrap gap-2">
          {d.can_follow && (
            <button onClick={toggleFollow} disabled={fbusy} data-testid="creator-profile-follow"
              className={`flex items-center gap-1 rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${d.following ? "border border-[#a03cff] bg-[#3a0d6e]/60 text-white" : "btn-brand text-white"}`}>
              {d.following ? <><UserCheck size={13} /> Following</> : <><UserPlus size={13} /> Follow</>}
            </button>
          )}
          {user?.role === "subscriber" && <button onClick={tip} data-testid="creator-profile-tip" className="flex items-center gap-1 rounded-full border border-[#d42be8]/50 px-4 py-1.5 text-xs font-semibold text-[#f0a8fa]"><Gift size={13} /> Send a tip</button>}
        </div>
        {!d.can_follow && !isGuestReviewer && <p data-testid="creator-profile-follow-note" className="mt-2 text-[0.65rem] text-[#6f6878]">Following is available to subscriber accounts, so follower counts stay genuine.</p>}

        <div className="mt-4 rounded-2xl glass p-4">
          {d.entitled ? (
            <p data-testid="creator-profile-member" className="flex items-center gap-2 text-sm font-semibold text-[#f0a8fa]"><BadgeCheck size={16} /> {isReview ? "Owner review access — all content unlocked" : isGuestReviewer ? "Complimentary reviewer access — no purchase needed" : "You have membership access"}</p>
          ) : isGuestReviewer ? (
            <p data-testid="creator-profile-reviewer-limited" className="text-sm text-[#a7a0af]">Membership content for this creator isn't included in your review access. Plan: {plan?.purchasable ? `${fmtMoney(plan.price)} / month` : "pricing coming soon"}.</p>
          ) : (
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold">{plan?.name}</p>
                <p className="text-xs text-[#a7a0af]">{plan?.purchasable ? `${fmtMoney(plan.price)} / month` : "Pricing coming soon"} · or get PEEPING ALL ACCESS</p>
              </div>
              {user?.role === "subscriber" && <button onClick={() => navigate("/app/membership")} data-testid="creator-profile-subscribe" className="btn-brand flex items-center gap-1 rounded-full px-4 py-2 text-xs font-semibold"><Crown size={14} /> Subscribe</button>}
            </div>
          )}
        </div>

        {d.upcoming_live.length > 0 && (
          <div className="mt-4 flex flex-col gap-2">
            {d.upcoming_live.map((s) => (
              <p key={s.id} className="flex items-center gap-2 text-xs text-[#c7c1cf]"><CalendarClock size={14} className="text-[#d42be8]" /> Live: {s.title} · {fmtDate(s.scheduled_for)}</p>
            ))}
          </div>
        )}

        <h2 className="mt-6 text-lg font-bold">Posts</h2>
        {d.items.length === 0 && <p data-testid="creator-profile-empty" className="mt-2 text-sm text-[#a7a0af]">No posts yet.</p>}
        <div className="mt-3 grid grid-cols-2 gap-3 pb-6">
          {d.items.map((it) => (
            <div key={it.id} className="flex flex-col gap-1">
              <button data-testid={`creator-item-${it.id}`} onClick={() => open(it)} className="relative aspect-[3/4] overflow-hidden rounded-2xl text-left">
                {it.cover_url ? <MediaImage url={it.cover_url} className="h-full w-full object-cover" /> : (
                  <div data-testid={`creator-post-text-${it.id}`} className="h-full w-full overflow-hidden bg-gradient-to-br from-[#2a0b3d] to-[#0c0a10] p-3 text-xs leading-relaxed text-white/85">{it.description}</div>
                )}
                {it.locked && <LockedOverlay label={it.access_level === "ppv" && it.ppv_price ? `Unlock ${fmtMoney(it.ppv_price)}` : undefined} />}
                <p className="absolute bottom-2 left-2 right-2 truncate text-xs font-semibold">{it.title}</p>
              </button>
              {isReview && <button onClick={() => unpublish(it)} data-testid={`review-unpublish-${it.id}`} className="flex items-center justify-center gap-1 rounded-lg border border-red-400/30 py-1 text-[0.65rem] text-red-300"><EyeOff size={11} /> Unpublish ({it.access_level})</button>}
            </div>
          ))}
        </div>
      </div>
      <Dialog open={!!post} onOpenChange={(o) => !o && setPost(null)}>
        <DialogContent data-testid="creator-post-dialog" className="max-h-[90vh] overflow-y-auto border-white/10 bg-[#0c0a10] text-white">
          <DialogHeader><DialogTitle>{post?.title}</DialogTitle></DialogHeader>
          {post?.description && <p className="whitespace-pre-line text-sm text-white/85">{post.description}</p>}
          {(post?.photo_urls || []).map((u) => <MediaImage key={u} url={u} className="w-full rounded-xl" />)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
